import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { ActionCell, BottomSheetComponent, DrawerItems, IconButton } from '@material-library';
import {
  BlendEvent,
  ENTITLEMENT,
  PageTitles,
  PermissionActions,
  PreviewType,
  pageTitleMap,
} from 'src/app/shared/models/enums';
import { SnackbarService } from '../../services/snackbar.service';
import { ShareService } from '../../services/share.service';
import { B64Pipe } from '../../pipes/b64.pipe';
import { Subscription, filter } from 'rxjs';
import { Store, select } from '@ngrx/store';
import * as fromRoot from '../../../reducers';
import { TransferItem, User } from '../../models';
import { environment } from 'src/environments/environment';
import { FeatureService } from '../../services/feature.service';
import { FileListService } from 'src/app/file-list/services/file-list.service';
import { TransferService } from 'src/app/transfer/services/queue/transfer.service';
import { FileItemService } from 'src/app/file/file-item.service';
import { IFile } from 'src/sync';
import { AppBarActionsService } from './app-bar-actions.service';
import { UserService } from 'src/app/core/user.service';
import { LoggerService } from 'src/app/core/logger.service';
import { RootLayoutConfig, RootLayoutConfigService } from './root-layout-config.service';
import { Location, TitleCasePipe } from '@angular/common';
import { BlendService } from '../../services/blend.service';
import { ProcessIndicatorService } from '../../services/process-indicator.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { SecuredTooltipComponent } from '../../components/secured-tooltip/secured-tooltip.component';
import { HistoryTrackerService } from 'src/app/core/history-tracker.service';
import { ApiService } from 'src/app/core/api.service';
import { TransferWidgetNavigationService } from '../../services/transfer-widget-navigation.service';

function mergeUrlParams(url, additionalParams): string {
  // Parse the URL.
  const urlObj = new URL(url, window.location.origin);

  // Iterate over the additional parameters.
  Object.entries(additionalParams).forEach(([key, value]) => {
    // This will add the parameter or override it if it already exists.
    urlObj.searchParams.set(key, value as any);
  });

  return urlObj.toString();
}

function urlsAreEqual(url1, url2): boolean {
  const u1 = new URL(url1, window.location.origin);
  const u2 = new URL(url2, window.location.origin);

  // Compare basic URL components: protocol, host (includes hostname and port), pathname, and hash.
  if (u1.protocol !== u2.protocol || u1.host !== u2.host || u1.pathname !== u2.pathname || u1.hash !== u2.hash) {
    return false;
  }

  // Convert GET parameters to arrays of [key, value] pairs and sort them.
  const params1 = Array.from(u1.searchParams.entries()).sort(([aKey], [bKey]) => aKey.localeCompare(bKey));
  const params2 = Array.from(u2.searchParams.entries()).sort(([aKey], [bKey]) => aKey.localeCompare(bKey));

  // If the number of parameters is different, the URLs are not equal.
  if (params1.length !== params2.length) {
    return false;
  }

  // Compare each parameter.
  for (let i = 0; i < params1.length; i++) {
    const [key1, value1] = params1[i];
    const [key2, value2] = params2[i];
    if (key1 !== key2 || value1 !== value2) {
      return false;
    }
  }

  return true;
}

@Component({
  selector: 'app-root-layout',
  templateUrl: './root-layout.component.html',
  styleUrls: ['./root-layout.component.scss'],
  animations: [
    trigger('fadeInOut', [
      state(
        'void',
        style({
          opacity: 0,
        }),
      ),
      transition(':enter, :leave', [animate(100)]),
    ]),
  ],
})
export class RootLayoutComponent implements OnInit, OnDestroy {
  public pageTitle: PageTitles;
  public pageTitleSuffix: string;
  public navItems: DrawerItems[] = [];
  public breadcrumbItems: any = {};
  public showSideNav = false;
  public isDrawerExpanded = false;
  public hasBackdrop = false;
  public showMenuIcon = false;
  public avtarInfo: any = {};
  public isPanelOpen = false;
  public isBottomSheetOpen = false;
  public avatarUrl: string;
  public user: User;
  private subscription: Subscription;
  private chargebeeSubscription: Subscription;
  public avatarEndpoint = `${environment.logohost}avatar/saved/`;
  private defaultLogo: string;
  public showBreadcrumb = false;
  @ViewChild('bottomSheetComponent')
  public bottomSheetComponent!: BottomSheetComponent;
  public fileActionsConfig: ActionCell;
  public breadcrumbActions: ActionCell;

  private avatarSize = 48;
  public openHelpDialog = false;
  public openContactSupportDialog = false;
  public previewAppbarConfig: any;
  public isPreviewLayout = false;
  public fileItem: IFile;
  public fileType = '';
  public previewType = PreviewType;
  public locId: number;
  public appBarActions?: ActionCell;
  public errcode: number | null = null;
  private errcodeSubscription: Subscription;
  private routerSubscription: Subscription;
  private fileListSubscription: Subscription;
  private breakpointSubscription: Subscription;
  private avatarSubscription: Subscription;
  private actionsSubscription: Subscription;
  private appbarSubscription: Subscription;
  private routerNavigationSubscription: Subscription;
  public isVault: boolean;

  protected rootLayoutConfig: RootLayoutConfig;
  public canGoBack = false;
  @ViewChild('panel')
  public panel: ElementRef | undefined;

  @ViewChild('secondaryTextTemplate')
  public secondaryTextTemplateRef: TemplateRef<any>;

  public isShowDivider = true;
  public showStartPageDialog = false;
  public startPagesOptions = [
    { icon: 'folder-sync', name: 'files' },
    { icon: 'vault', name: 'vault' },
    { icon: 'star', name: 'starred' },
  ];
  public selectedStartPage = '';
  public isSmallScreen = false;

  @HostListener('document:click', ['$event'])
  public onClick(event: MouseEvent): void {
    if (this.panel && !this.panel.nativeElement.contains(event.target)) {
      this.isPanelOpen = false;
    }
  }

  constructor(
    private router: Router,
    public route: ActivatedRoute,
    public breakpointObserver: BreakpointObserver,
    public snackbarService: SnackbarService,
    public shareService: ShareService,
    public b64Pipe: B64Pipe,
    private store: Store<fromRoot.State>,
    private featureService: FeatureService,
    private fileListService: FileListService,
    public transferService: TransferService,
    private changeDetectorRef: ChangeDetectorRef,
    private fileItemService: FileItemService,
    private actionsService: AppBarActionsService,
    private userService: UserService,
    private loggerService: LoggerService,
    private appBarService: RootLayoutConfigService,
    private location: Location,
    private blendService: BlendService,
    public processIndicatorService: ProcessIndicatorService,
    private titleCasePipe: TitleCasePipe,
    private overlay: Overlay,
    private historyTrackerService: HistoryTrackerService,
    private apiService: ApiService,
    private transferWidgetNavigationService: TransferWidgetNavigationService,
  ) {}

  public async ngOnInit(): Promise<void> {
    this.user = this.userService.getUser();
    this.subscription = this.store.pipe(select(fromRoot.getAuthUser)).subscribe((data) => {
      this.user = data;
      this.updateAvatarInfo(this.avatarUrl);
      this.initializeDrawer();
      this.shareService.updateTheme();
    });

    this.errcodeSubscription = this.appBarService.errcode$.subscribe((code) => {
      this.errcode = code;
    });

    this.breakpointSubscription = this.breakpointObserver
      .observe(['(max-width: 904px)', '(max-width: 599px)', '(max-width: 1239px)'])
      .subscribe((state: BreakpointState) => {
        if (state.breakpoints['(max-width: 599px)']) {
          // If the screen size is less than or equal to 599px  wide,
          // hide side navigation bar, show menu icon in toolbar and enable bottomsheet
          this.showSideNav = false;
          this.hasBackdrop = true;
          this.showMenuIcon = true;
          this.isBottomSheetOpen = true;
          this.isPanelOpen = false;
          this.avatarSize = 32;
          this.isShowDivider = false;
          this.isSmallScreen = true;
        } else if (state.breakpoints['(max-width: 904px)']) {
          // If the screen size is less than or equal to 904px, set the drawer to be always expanded and visible
          this.showSideNav = false;
          this.hasBackdrop = true;
          this.showMenuIcon = true;
          this.bottomSheetComponent?.dismissBottomSheet();
          this.avatarSize = 32;
          this.isShowDivider = true;
          this.isSmallScreen = true;
        } else if (state.breakpoints['(max-width: 1239px)']) {
          this.showSideNav = true;
          this.hasBackdrop = false;
          this.isDrawerExpanded = false;
          this.showMenuIcon = false;
          this.isBottomSheetOpen = false;
          this.bottomSheetComponent?.dismissBottomSheet();
          this.avatarSize = 40;
          this.isShowDivider = true;
          this.isSmallScreen = false;
        } else {
          // If the screen size is greater than 904px, set the drawer to be collapsed and hide bottomsheet
          this.showSideNav = true;
          this.hasBackdrop = false;
          this.isDrawerExpanded = false;
          this.showMenuIcon = false;
          this.isBottomSheetOpen = false;
          this.bottomSheetComponent?.dismissBottomSheet();
          this.avatarSize = 48;
          this.isShowDivider = true;
          this.isSmallScreen = false;
        }

        this.updateAvatarInfo(this.avatarUrl);
        this.changeDetectorRef.detectChanges();
      });

    this.defaultLogo = this.user?.avatar?.avatar_data;
    this.avatarUrl = this.defaultLogo ? `${this.avatarEndpoint}${this.user?.id}` : '';

    this.avatarSubscription = this.shareService.avatarUrl$.subscribe((updatedAvatarUrl) => {
      this.updateAvatarInfo(updatedAvatarUrl);
    });
    this.updateAvatarInfo(this.avatarUrl);

    this.initializeDrawer();

    this.fileActionsConfig = {
      actions: [
        {
          name: 'File info',
          icon: 'info',
          onClick: (rowData: any, event: Event): void => {
            this.shareService.openPanel();
          },
        },
        {
          name: 'Link',
          icon: 'link',
          onClick: (rowData: any, event: Event): void => {
            // empty
          },
        },
        {
          name: 'Star',
          icon: 'star',
          onClick: (rowData: any, event: Event): void => {
            // empty
          },
        },
        {
          name: 'Delete',
          icon: 'delete',
          onClick: (rowData: any, event: Event): void => {
            // empty
          },
        },
        {
          name: 'Team Share',
          icon: 'folder-shared-team',
          onClick: (rowData: any, event: Event): void => {
            // empty
          },
        },
        {
          name: 'Move',
          icon: 'move-file-folder',
          onClick: (rowData: any, event: Event): void => {
            // empty
          },
        },
        {
          name: 'Rename',
          icon: 'move-file-folder',
          onClick: (rowData: any, event: Event): void => {
            // empty
          },
        },
        {
          name: 'Version history',
          icon: 'clock',
          onClick: (rowData: any): void => {
            if (this.fileItem) {
              const syncId = this.fileItem?.sync_id;
              this.fileListService.onViewHistory(syncId);
            }
          },
        },
      ],
      limit: 5,
      showActionsInBottomSheet: true,
      rowData: {
        id: 1,
        position: 1,
        name: 'Hydrogen',
        description: 'this is description',
        weight: 1.0079,
        symbol: 'H',
        updatedDate: new Date(),
        headIcon: 'folder',
        fileType: 'file',
        image: 'https://material.angular.io/assets/img/examples/shiba2.jpg',
      },
    };
    this.actionsSubscription = this.actionsService.currentData.subscribe((data) => {
      this.appBarActions = data;
    });

    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.checkSupportFormRoute();
        this.changeDetectorRef.detectChanges();
      });

    this.routerNavigationSubscription = this.router.events.subscribe(() => {
      this.handleRouteChanges();
    });
    this.handleRouteChanges();
    this.checkSupportFormRoute();

    this.appBarService.currentData$.subscribe((data) => {
      this.rootLayoutConfig = data;
      if (!data) return;
      if ('pageTitle' in data) {
        this.pageTitle = data.pageTitle as PageTitles;
      }
      if (data.breadcrumbActions && this.breadcrumbItems) {
        this.breadcrumbItems = { ...this.breadcrumbItems, actionsConfig: data.breadcrumbActions };
      }
    });

    this.subscribeBreadcrumbItems();
  }

  protected get appBarOverride(): any {
    if (this.rootLayoutConfig?.appBar) {
      return {
        showLeadIcon: this.showMenuIcon,
        leadIcon: { iconName: 'menu', disabled: false, size: 'large' },
        onLeadIconClick: this.onLeadIconClick,
        showRightActions: true,
        showTrailIcon1: true,
        trailIcon1: { iconName: 'help', disabled: false, size: 'large' },
        showAvatar: true,
        avatar: this.avtarInfo,
        showSecondaryText: true,
        ...this.rootLayoutConfig.appBar,
        actionsConfig: this.rootLayoutConfig.appBar?.actionsConfig || { actions: [] },
      };
    }

    return null;
  }

  protected get sideNavConfig(): any {
    if (this.errcode || this.fileType === PreviewType.OFFICE_FSV || this.fileType === PreviewType.OFFICE_FSE) {
      return null;
    }

    return {
      navItems: this.navItems,
      isExpanded: this.isDrawerExpanded,
      hasBackdrop: this.hasBackdrop,
      backdropClick: this.onBackdropClick,
      closeSideNav: this.onCloseSideNav,
    };
  }

  // Function to handle data fetching for preview
  public async handleDataFetch(locId: number): Promise<void> {
    if (locId) {
      if (this.fileItem?.sync_id !== locId) {
        this.previewAppbarConfig = {
          showLeadIcon: true,
          leadIcon: { iconName: 'arrow-left', disabled: false, size: 'large' },
          heading: ' ',
          prominent: false,
          showRightActions: true,
          showTrailIcon1: this.user?.is_pro ? true : false,
          trailIcon1: { iconName: 'help', disabled: false, size: 'large' },
          showAvatar: true,
          avatar: this.avtarInfo,
          secondaryText: ' ',
          secondaryTextTemplate: this.secondaryTextTemplateRef,
        };
        const result = await this.apiService.execute('pathget', { sync_id: locId });
        this.fileItem = await this.fileItemService.format(result);
      }
      this.updatePreviewConfig();
    } else {
      this.previewAppbarConfig = null;
    }
  }

  private handleRouteChanges(): void {
    let activeRoute = this.route;

    while (activeRoute.firstChild) {
      activeRoute = activeRoute.firstChild;
    }

    activeRoute.params
      .pipe(
        filter((params) => +params['id'] !== this.locId), // Avoid multiple API calls for the same locId
      )
      .subscribe((params) => {
        this.fileType = params['type'];
        this.locId = 'id' in params ? +params['id'] : undefined;

        this.handleDataFetch(this.locId); // Handle API call only when locId changes
      });

    this.setPageTitleFromRoute();
    this.updateDrawer(this.pageTitle);
  }

  private updatePreviewConfig = (): void => {
    if (!this.fileItem) {
      return;
    }
    const timestamp = this.fileItem.usertime ? this.shareService.formatTimestamp(this.fileItem.usertime) : '';

    this.previewAppbarConfig = {
      showLeadIcon: true,
      leadIcon: { iconName: 'arrow-left', disabled: false, size: 'large' },
      heading: this.fileItem.name,
      prominent: false,
      showRightActions: true,
      showTrailIcon1: true,
      trailIcon1: { iconName: 'help', disabled: false, size: 'large' },
      showAvatar: true,
      avatar: this.avtarInfo,
      secondaryText: `Modified on ${timestamp}`,
      ternaryText: this.fileItem.is_publink === 1 ? 'Shared via link' : '',
      secondaryTextTemplate: this.secondaryTextTemplateRef,
    };
  };
  protected onPreviewBackClick = (): void => {
    this.location.back();
  };
  public subscribeBreadcrumbItems = (): void => {
    this.fileListSubscription = this.fileListService.getListSubscription().subscribe((data) => {
      if (data.parents && data.parents.length > 1) {
        const baseUrlMap = {
          starred: { label: 'Starred', url: '/starred' },
          vault: { label: 'Vault', url: '/files' },
          files: { label: 'Files', url: '/files' },
        };

        if (baseUrlMap[this.getUrlBase()]) {
          const root =
            data.parents[0].metaname === 'web'
              ? { label: 'Vault', url: '/vault' }
              : baseUrlMap[this.getUrlBase()] || { label: '', url: '' };

          const parentItems = data.parents.slice(1).map((parent) => ({
            label: parent.name,
            url: `${baseUrlMap[this.getUrlBase()].url}/${parent.loc_id}`,
          }));

          this.breadcrumbItems = {
            items: root.label ? [root, ...parentItems] : parentItems,
            maxItem: root.label ? 3 : 2,
            actionsConfig: this.rootLayoutConfig?.breadcrumbActions,
          };

          const lastBreadcrumb = this.breadcrumbItems.items[this.breadcrumbItems.items.length - 1];
          if (this.route.snapshot.queryParams.showDeleted === '1') {
            lastBreadcrumb.showTrailAdornment = true;
            lastBreadcrumb.trailAdornment = {
              type: 'text',
              size: 'large',
              textContent: '(Deleted)',
            };
          } else if (data.cwd.is_share_root) {
            lastBreadcrumb.showTrailAdornment = true;
            lastBreadcrumb.trailAdornment = {
              type: 'icon',
              size: 'large',
              iconName: 'team-share',
            };
          }
        }
      } else {
        this.breadcrumbItems = { items: [] };
        this.setPageTitleFromRoute();
      }
    });
  };

  private baseUrl(): string {
    // matches the first segment of the url, including the leading slash
    // eg. /files/1234 -> /files, /files?view=list -> /files
    return this.router.url.match(/^(\/[^\/\?]*)/)[0];
  }

  public onBreadcrumbClick(event): void {
    const viewMode = this.route.snapshot.queryParams['view'] || 'list';
    this.router.navigate([event.url], {
      queryParams: { view: viewMode },
      queryParamsHandling: 'merge',
    });
  }

  public onBreadcrumbBackBtn(event): void {
    const viewMode = this.route.snapshot.queryParams['view'] || 'list';
    const goBackToPage = this.route.snapshot.queryParams['goBackToPage'] || '';
    if (goBackToPage) {
      this.router.navigate([goBackToPage], {});
      return;
    }

    if (
      urlsAreEqual(
        mergeUrlParams(event.url, { view: viewMode }),
        this.historyTrackerService.getPreviousHistoryItem()?.url,
      )
    ) {
      window.history.back();
      return;
    }

    this.router.navigate([event.url], {
      queryParams: { view: viewMode },
      queryParamsHandling: 'merge',
    });
  }

  public onBreadcrumbMenuItem = (event): void => {
    const viewMode = this.route.snapshot.queryParams['view'] || 'list';
    this.router.navigate([event.value], {
      queryParams: { view: viewMode },
      queryParamsHandling: 'merge',
    });
  };

  private updateAvatarInfo(updatedAvatarUrl: string): void {
    this.avatarUrl = updatedAvatarUrl;
    this.avtarInfo = {
      type: 'avatar',
      size: this.avatarSize,
      url: this.avatarUrl,
      initials: this.b64Pipe.transform(this.user?.display_name),
    };
  }

  public ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.errcodeSubscription) {
      this.errcodeSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.fileListSubscription) {
      this.fileListSubscription.unsubscribe();
    }
    if (this.breakpointSubscription) {
      this.breakpointSubscription.unsubscribe();
    }
    if (this.avatarSubscription) {
      this.avatarSubscription.unsubscribe();
    }
    if (this.actionsSubscription) {
      this.actionsSubscription.unsubscribe();
    }
    if (this.appbarSubscription) {
      this.appbarSubscription.unsubscribe();
    }
    if (this.routerNavigationSubscription) {
      this.routerNavigationSubscription.unsubscribe();
    }
  }

  private getUrlBase(): string {
    let urlSegments = this.router.url.split('/')[1];
    if (urlSegments && urlSegments.includes('?')) {
      urlSegments = urlSegments.split('?')[0];
    }
    return urlSegments;
  }

  protected isCustomLayout(): boolean {
    if (this.rootLayoutConfig?.isCustomLayout || this.isPreviewLayout) {
      return true;
    }
    const specialPages = [
      'Files',
      'Vault',
      'Dashboard',
      'Version history',
      'Shares',
      'Recents',
      'Users',
      'Users & roles',
    ]; // Add other page titles as needed
    const urlSegments = this.getUrlBase();
    const pageTitle = pageTitleMap[urlSegments] || '';
    return specialPages.includes(pageTitle);
  }

  protected onLeadIconClick = (): void => {
    if (this.canGoBack) {
      this.onPreviewBackClick();
      return;
    }
    this.showSideNav = !this.showSideNav;
    this.isDrawerExpanded = !this.isDrawerExpanded;
  };

  protected onAvatarClick = (event: MouseEvent): void => {
    event?.stopPropagation();
    if (window.innerWidth <= 599) {
      this.bottomSheetComponent?.openBottomSheet();
    } else {
      this.isBottomSheetOpen = false;
      this.isPanelOpen = !this.isPanelOpen;
    }
    this.blendService.track(BlendEvent.BUTTON_CLICKED, { button_text: 'Avatar' });
  };

  private generateNavUrl(route: string): string {
    return `/${route}`;
  }

  private onBackdropClick = (): void => {
    this.showSideNav = false;
    this.isDrawerExpanded = false;
  };

  protected onCloseSideNav = (): void => {
    this.onBackdropClick();
  };

  protected closePanel(): void {
    this.isPanelOpen = false;
  }

  private checkSupportFormRoute(): void {
    const isSupportFormRoute = this.router.url.startsWith('/support-form');
    if (isSupportFormRoute) {
      this.openContactSupportDialog = true;
      this.openHelpDialog = true;
    } else {
      this.openContactSupportDialog = false;
    }
  }

  public trackByIndex(index: number): number {
    return index;
  }

  protected onHelpIconClick = (): void => {
    this.openHelpDialog = true;
    this.blendService.track(BlendEvent.HELP_EVENT);
  };

  private updateDrawer(pageTitle: PageTitles): void {
    if (this.isPreviewLayout || this.rootLayoutConfig?.pageTitle) {
      return;
    }
    const nestedChild = [
      { parent: PageTitles.FILES, child: PageTitles.FILE_HISTORY, path: '/files/file-history' },
      { parent: PageTitles.VAULT, child: PageTitles.FILE_HISTORY, path: '/vault/file-history' },
    ];

    let lastSelected: DrawerItems;
    // Add other page titles as needed
    for (const item of this.navItems) {
      if (item.label === pageTitle) {
        if (lastSelected) {
          lastSelected.isSelected = false;
        }
        item.isSelected = true;
        lastSelected = item;
      } else {
        item.isSelected = false;
      }
      for (const child of nestedChild) {
        if (child.child === pageTitle && child.parent === item.label && this.router.url.includes(child.path)) {
          if (lastSelected) {
            lastSelected.isSelected = false;
          }
          item.isSelected = true;
          lastSelected = item;
          break;
        }
      }
    }
  }

  private setPageTitleFromRoute(): void {
    const pathSegments = this.router.url.split('/');
    let urlSegments = this.getUrlBase();
    if (urlSegments && urlSegments.includes('?')) {
      urlSegments = urlSegments.split('?')[0];
    }
    this.isPreviewLayout = urlSegments === 'file';
    const allowBackIconOnPages = []; // Add other page back button as needed
    let canGoBack = false;
    let pageTitle: PageTitles;
    const extraCheckForNestedPath = [pathSegments[2]]; //add nested page path
    for (const path of extraCheckForNestedPath) {
      if (allowBackIconOnPages.includes(path)) {
        pageTitle = pageTitleMap[path] as PageTitles;
        canGoBack = true;
        break;
      } else {
        canGoBack = false;
      }
    }

    if (!this.rootLayoutConfig?.pageTitle) {
      if (pageTitle) {
        this.pageTitle = pageTitle;
      } else {
        this.pageTitle = pageTitleMap[urlSegments] as PageTitles;
      }
    }

    if (
      ['/starred', '/vault', '/files'].includes(this.baseUrl()) &&
      this.route.snapshot.queryParams.showDeleted === '1'
    ) {
      this.pageTitleSuffix = '(Deleted)';
    } else {
      delete this.pageTitleSuffix;
    }

    this.canGoBack = canGoBack;
  }

  protected useBreadcrumbItems(): boolean {
    return this.rootLayoutConfig?.useBreadcrumbItems;
  }

  protected getLeadIconForAppBar = (): IconButton => {
    if (this.canGoBack) {
      return {
        iconName: 'arrow-left',
        disabled: false,
        size: 'large',
      };
    }
    return {
      iconName: 'menu',
      disabled: false,
      size: 'large',
    };
  };

  protected closeStartPageDialog = (): void => {
    this.showStartPageDialog = false;
  };
  protected disableStartPageDialogButtonExecute = (): boolean => {
    return !this.selectedStartPage;
  };
  protected onActionStartPageDialogButtonClick = async (): Promise<void> => {
    try {
      await this.userService.setUserInfo({ dashboard: this.selectedStartPage });
      this.showStartPageDialog = false;
      this.snackbarService.showMessage(
        'success',
        `Start page successfully changed to “${this.titleCasePipe.transform(this.selectedStartPage)} items”`,
      );
      this.userService.refresh();
    } catch (error) {
      this.snackbarService.showMessage('critical', 'Couldn’t complete action. Try again.');
      this.showStartPageDialog = false;
    }
  };
  protected openStartPageDialog = (event): void => {
    this.showStartPageDialog = event;
    this.isPanelOpen = false;
    this.selectedStartPage = '';
    const currentSelectedPage = this.userService.get('dashboard');
    const startPageOptions = [
      { icon: 'folder-sync', name: 'files' },
      { icon: 'vault', name: 'vault' },
      { icon: 'star', name: 'starred' },
    ];
    const ind = startPageOptions.findIndex((item) => currentSelectedPage == item.name);
    if (ind > -1) {
      const newOptions = [...startPageOptions];
      newOptions[0] = startPageOptions[ind];
      newOptions[ind] = startPageOptions[0];
      this.startPagesOptions = newOptions;
    } else {
      this.startPagesOptions = startPageOptions;
    }
  };

  protected showSecuredTooltip = (event: Event): void => {
    const targetEl = event.target as Element;
    const buttonEl = targetEl.closest('sync-icon-button');
    const overlayRef = this.overlay.create({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(buttonEl)
        .withPositions([
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
          },
        ]),
      hasBackdrop: true,
      backdropClass: 'secured-tooltip-backdrop',
    });

    overlayRef.attach(new ComponentPortal(SecuredTooltipComponent));

    overlayRef.backdropClick().subscribe(() => {
      overlayRef?.detach();
    });

    overlayRef.overlayElement.addEventListener('click', () => {
      overlayRef.detach();
    });
  };

  protected get fullPageTitle(): string {
    if (this.pageTitleSuffix) {
      return `${this.pageTitle} ${this.pageTitleSuffix}`;
    } else {
      return this.pageTitle;
    }
  }

  private initializeDrawer(): void {
    const isLegacy =
      (this.user.is_multi_admin || !this.user.is_multi_admin) && !this.user.is_multi_child && !this.user.is_multi_new;
    const navItems = [
      {
        drawerItem: {
          label: PageTitles.FILES,
          leadAdornmentConfig: {
            enabled: true,
            type: 'icon',
            size: 'large',
            iconName: 'folder',
          },
        },
        navConfig: {
          route: 'files',
        },
      },
      {
        drawerItem: {
          label: PageTitles.VAULT,
          leadAdornmentConfig: {
            enabled: true,
            type: 'icon',
            size: 'large',
            iconName: 'vault',
          },
        },
        navConfig: {
          route: 'vault',
        },
      },
      {
        drawerItem: {
          label: PageTitles.SHARES,
          leadAdornmentConfig: {
            enabled: true,
            type: 'icon',
            size: 'large',
            iconName: 'shared-files-folders',
          },
        },
        navConfig: {
          route: 'shares',
        },
      },
      {
        drawerItem: {
          label: PageTitles.STARRED,
          leadAdornmentConfig: {
            enabled: true,
            type: 'icon',
            size: 'large',
            iconName: 'star',
          },
        },
        navConfig: {
          route: 'starred',
        },
      },
      {
        drawerItem: {
          label: PageTitles.RECENTS,
          leadAdornmentConfig: {
            enabled: true,
            type: 'icon',
            size: 'large',
            iconName: 'clock',
          },
        },
        navConfig: {
          route: 'recents',
        },
      },
      { label: '', divider: true },
      ...((this.userService.checkPermission(PermissionActions.VIEW_USERS) ||
        this.userService.checkPermission(PermissionActions.VIEW_ROLES)) &&
      this.userService.get('plan_id') > 99
        ? [
            {
              drawerItem: {
                label:
                  isLegacy || !this.userService.isFeatureAllowed(ENTITLEMENT.RBAC) ? 'Users' : PageTitles.USERS_ROLES,
                leadAdornmentConfig: {
                  enabled: true,
                  type: 'icon',
                  size: 'large',
                  iconName: 'team',
                },
              },
              url: (): string => {
                if (isLegacy) {
                  return this.generateNavUrl('users');
                } else {
                  if (
                    !this.userService.checkPermission(PermissionActions.VIEW_USERS) &&
                    this.userService.isFeatureAllowed(ENTITLEMENT.RBAC) &&
                    this.userService.checkPermission(PermissionActions.VIEW_ROLES)
                  ) {
                    return this.generateNavUrl('users/roles');
                  } else {
                    return this.generateNavUrl('users');
                  }
                }
              },
            },
          ]
        : []),
      {
        drawerItem: {
          label: PageTitles.SETTINGS,
          leadAdornmentConfig: {
            enabled: true,
            type: 'icon',
            size: 'large',
            iconName: 'settings',
          },
        },
        navConfig: {
          route: 'settings',
        },
      },
      { label: '', divider: true },
    ];

    this.navItems = navItems.map((item) => {
      if (item.divider) {
        return item;
      } else {
        return {
          onClick: (): void => {
            if (this.isSmallScreen) {
              this.onBackdropClick();
            }
          },
          ...item.drawerItem,
          url: item.url ? item.url() : this.generateNavUrl(item.navConfig.route),
        };
      }
    }) as DrawerItems[];

    if (this.pageTitle) {
      this.updateDrawer(this.pageTitle);
    }
  }

  public async handleOpenFolder(data: { transferItem: TransferItem; id: number }): Promise<void> {
    if (!data?.id) return;
    await this.router.navigate(['/files', data.id]);
  }

  public async handlePreviewFile(transferItem: TransferItem): Promise<void> {
    const file = this.transferWidgetNavigationService.transformTransferItemToFileItem(transferItem);
    const url = this.fileListService.getItemLink(file);
    await this.router.navigate([url]);
  }
}
